import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Copy, ChevronDown, ChevronUp, ChevronRight } from "lucide-react";
import api, { apiError } from "../lib/api";
import { Money } from "../context/PrivacyContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { refreshCatalogs } from "../components/MoneyMovementFields";

const EMPTY = { name: "", type: "Bancos y Caja", balance: "", purchase_value: "", acquisition_date: "", owner_member_id: null, notes: "" };

const GROUP_STORAGE_KEY = "activos_expanded_groups_v1";

export default function Activos() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [types, setTypes] = useState([]);
  const [members, setMembers] = useState([]);
  const [detail, setDetail] = useState(false);
  const [expanded, setExpanded] = useState(() => {
    try {
      const saved = localStorage.getItem(GROUP_STORAGE_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });

  const toggleGroup = (key) => {
    setExpanded((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem(GROUP_STORAGE_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const load = () => api.get("/assets").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/asset_types").then((r) => setTypes(r.data.filter((c) => c.active).map((c) => c.name)));
    api.get("/family").then((r) => setMembers(r.data.members || []));
  }, []);

  const duplicate = (a) => {
    setForm({ ...EMPTY, name: `${a.name} (copia)`, type: a.type, balance: a.balance, purchase_value: a.purchase_value ?? "", acquisition_date: a.acquisition_date || "", owner_member_id: a.owner_member_id || null, notes: a.notes || "" });
    setEditId(null);
    setOpen(true);
    toast.info("Datos copiados. Ajusta lo que necesites y guarda");
  };

  const submit = async () => {
    try {
      const payload = {
        ...form,
        balance: parseFloat(String(form.balance).replace(",", ".")) || 0,
        purchase_value: form.purchase_value === "" ? null : parseFloat(String(form.purchase_value).replace(",", ".")) || 0,
        acquisition_date: form.acquisition_date || null,
        notes: form.notes || null,
      };
      if (editId) await api.put(`/assets/${editId}`, payload);
      else await api.post("/assets", payload);
      refreshCatalogs();
      toast.success(editId ? "Activo actualizado" : "Activo añadido");
      setOpen(false); setForm(EMPTY); setEditId(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id) => {
    await api.delete(`/assets/${id}`);
    refreshCatalogs();
    toast.success("Activo eliminado");
    load();
  };

  const total = (items || []).reduce((s, a) => s + a.balance, 0);

  // Agrupar por categoría (type)
  const groups = (items || []).reduce((acc, a) => {
    const key = a.type || "Sin categoría";
    if (!acc[key]) acc[key] = { items: [], total: 0 };
    acc[key].items.push(a);
    acc[key].total += a.balance;
    return acc;
  }, {});
  const groupKeys = Object.keys(groups).sort((a, b) => groups[b].total - groups[a].total);

  return (
    <div className="space-y-6" data-testid="activos-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tighter">Activos</h1>
          <p className="text-sm text-muted-foreground mt-1">Todo lo que tienes, en un vistazo.</p>
        </div>
        <Button data-testid="add-asset-button" onClick={() => { setForm(EMPTY); setEditId(null); setOpen(true); }}>
          <Plus size={16} className="mr-1" /> Añadir activo
        </Button>
      </div>

      <div className="border border-border rounded-lg p-5">
        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold">Total activos</div>
        <Money value={total} testId="total-activos" className="block mt-1 text-3xl font-semibold tabular" />
      </div>

      <div className="border border-border rounded-lg divide-y divide-border" data-testid="assets-list">
        {items === null ? (
          <div className="p-5 text-sm text-muted-foreground">Cargando…</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Aún no hay activos. Añade tu primer banco, tarjeta o efectivo.</div>
        ) : (
          groupKeys.map((key) => {
            const isOpen = expanded[key] !== false; // por defecto abiertos
            const g = groups[key];
            return (
              <div key={key} data-testid={`group-${key}`}>
                <button
                  data-testid={`group-toggle-${key}`}
                  onClick={() => toggleGroup(key)}
                  className="w-full flex items-center justify-between p-4 bg-secondary/40 hover:bg-secondary/70 transition-colors text-left"
                >
                  <div className="flex items-center gap-2">
                    {isOpen ? <ChevronDown size={16} className="text-muted-foreground" /> : <ChevronRight size={16} className="text-muted-foreground" />}
                    <span className="font-semibold text-sm">{key}</span>
                    <span className="text-xs text-muted-foreground">({g.items.length})</span>
                  </div>
                  <Money value={g.total} className="font-semibold tabular text-sm" />
                </button>
                {isOpen && (
                  <div className="divide-y divide-border">
                    {g.items.map((a) => (
                      <div key={a.id} className="flex items-center justify-between p-4 pl-10 hover:bg-secondary/30 transition-colors">
                        <div>
                          <div className="font-medium text-sm">{a.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {a.owner_member_id && members.find((m) => m.id === a.owner_member_id) ? `${members.find((m) => m.id === a.owner_member_id).name}` : ""}
                            {a.owner_member_id && a.purchase_value != null && a.purchase_value > 0 ? " · " : ""}
                            {a.purchase_value != null && a.purchase_value > 0 ? `compra ${a.purchase_value.toLocaleString("es-ES")} €` : ""}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Money value={a.balance} className="font-semibold tabular" />
                          <button data-testid={`duplicate-asset-${a.id}`} onClick={() => duplicate(a)} title="Duplicar" className="text-muted-foreground hover:text-foreground"><Copy size={15} /></button>
                          <button data-testid={`edit-asset-${a.id}`} onClick={() => { setForm({ ...EMPTY, name: a.name, type: a.type, balance: a.balance, purchase_value: a.purchase_value ?? "", acquisition_date: a.acquisition_date || "", owner_member_id: a.owner_member_id || null, notes: a.notes || "" }); setEditId(a.id); setOpen(true); }} className="text-muted-foreground hover:text-foreground"><Pencil size={15} /></button>
                          <button data-testid={`delete-asset-${a.id}`} onClick={() => remove(a.id)} className="text-muted-foreground hover:text-red-600"><Trash2 size={15} /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Editar activo" : "Nuevo activo"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input data-testid="asset-name-input" placeholder="Nombre (ej. BBVA)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger data-testid="asset-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
            <Input data-testid="asset-balance-input" placeholder="Valor actual (€)" inputMode="decimal" value={form.balance} onChange={(e) => setForm({ ...form, balance: e.target.value })} />
            <button data-testid="asset-detail-toggle" onClick={() => setDetail(!detail)} className="flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground">
              {detail ? <ChevronUp size={13} /> : <ChevronDown size={13} />} Valoración y propiedad (opcional)
            </button>
            {detail && (
              <div className="space-y-3 border-t border-border pt-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-muted-foreground">Valor de compra (€)</label>
                    <Input data-testid="asset-purchase-input" inputMode="decimal" value={form.purchase_value} onChange={(e) => setForm({ ...form, purchase_value: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Fecha adquisición</label>
                    <Input data-testid="asset-acquisition-input" type="date" value={form.acquisition_date} onChange={(e) => setForm({ ...form, acquisition_date: e.target.value })} />
                  </div>
                </div>
                <Select value={form.owner_member_id || "none"} onValueChange={(v) => setForm({ ...form, owner_member_id: v === "none" ? null : v })}>
                  <SelectTrigger data-testid="asset-owner-select"><SelectValue placeholder="Propietario" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Propietario: familiar / sin asignar</SelectItem>
                    {members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input data-testid="asset-notes-input" placeholder="Documentación / notas" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
            )}
            <Button data-testid="asset-submit-button" onClick={submit} disabled={!form.name || form.balance === ""} className="w-full">
              {editId ? "Guardar cambios" : "Añadir"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
